import { Ref, Collection, RefObject, HandlersCollection, RegexExtractor } from "@tblabs/truffle";
import { DeviceConfig } from "../Models/DeviceConfig";
import { ConnectionStatus } from "../Models/DeviceConnectionStatus";
import { Stats } from "../Models/DeviceProfile";
import { DeviceRecord } from "../Models/DeviceRecord";
import { PictureForWebClient } from "../Models/PictureForWebClient";
import { RawDeviceProfile } from "../Models/RawDeviceProfile";
import { WatcherProfile } from "../Models/WatcherProfile";
import { CopyToClipboard } from "../Utils/CopyToClipboard";
import { CustomerAccessMessageBuilder } from "./CustomerAccessMessageBuilder";
import { DeviceSettingsForAdmin } from "./DeviceSettingsForAdmin";
import { HotspotInfo } from "./HotspotInfo";
import { HotspotType } from "./HotspotType";
import { LedStatus } from "./LedStatus";
import { Server } from "./Server";
import { IoState } from "./IoState";


export class Device
{
    public Id: string;
    public Version: string;
    public ConnectionStatus = new Ref<ConnectionStatus>(ConnectionStatus.Unset);
    public Password = new Ref<string>("");
    public Watchers = new Collection<WatcherProfile>();
    public Config: DeviceConfig;
    public Stats = new Stats();
    public Thumbnail = new RefObject<PictureForWebClient | null>(null);
    public Record: DeviceRecord;
    public IoState: IoState | null = null;
    public Malfunctions: string[] = [];

    public PictureInfo = new Ref<string>(this.MakePictureInfo());

    //#region Events
    public OnShellResult = new HandlersCollection<(result: string, cmd: string) => void>();
    public OnFileLoad = new HandlersCollection<(dir: string, fileContent: string) => void>();
    public OnFileSaved = new HandlersCollection<(dir: string) => void>();
    public OnFileProblem = new HandlersCollection<(problem: string) => void>();
    public OnLedStatusChange = new HandlersCollection<(status: string) => void>();
    public OnIoStateUpdate = new HandlersCollection<(ioState: IoState) => void>();
    //#endregion Events

    constructor(public _server: Server, raw: RawDeviceProfile)
    {
        this.Id = raw.Id;

        this.Update(raw);

        this.ShellEngineInit();
    }

    //#region Profile
    public Update(raw: RawDeviceProfile): void
    {
        this.Version = raw.Version;
        this.ConnectionStatus.value = raw.ConnectionStatus;
        this.Password.value = raw.Password;
        this.Watchers.Load(...raw.Watchers.map(x => new WatcherProfile(x)));
        this.Stats.PicturesCount.value = raw.Stats.PicturesCount;
        this.Stats.PicturesSize.value = raw.Stats.PicturesSize;
        this.Stats.RebootsCount.value = raw.Stats.RebootsCount;
        this.Thumbnail.object = raw.Thumbnail;
        this.Config = raw.Config;
        this.Record = raw.Record;
        this.IoState = raw.IoState;
        this.Malfunctions = raw.Malfunctions;

        this.PictureInfo.value = this.MakePictureInfo();
    }

    public async GetCurrentProfile(): Promise<boolean>
    {
        return await this._server.Request("get-device", [this.Id],
            (raw: RawDeviceProfile) =>
            {
                this.Update(raw);
            },
            (errorMessage: string) =>
            {
                console.log(`Fetching profile of "${this.Id}" failed: ${errorMessage}`);
            });
    }

    public async RequestIoState(): Promise<IoState>
    {
        return await this.Request<IoState>("get-io-state");
    }
    //#endregion Profile

    public get ServerId(): string
    {
        return this._server.Id;
    }

    //#region Props
    private GetWifiName(): string
    {
        if (this.Config?.wifi?.includes("ESSID:")) 
        {
            const regexExtractor = new RegexExtractor(/ESSID:"([\w\d]+)"/gm);
            return regexExtractor.Extract(this.Config.wifi);
        }
        else return this.Config?.wifi || "";
    }
    public get Hotspot(): HotspotInfo
    {
        let wifi = this.GetWifiName();
        let type = HotspotType.Unknown;

        if (wifi == "" || wifi == undefined)
            type = HotspotType.Unknown;
        else if (wifi == "hotspot0")
            type = HotspotType.Home;
        else if (wifi == "hotspot1" || wifi == "Hotspot1")
            type = HotspotType.Initial;
        else if (wifi.startsWith("T")) // TX..., TY... etc.
            type = HotspotType.Dedicated;
        else if (["Orange Free Wifi 5G", "Sala wykladowa C14-F", "VP3C4F44Y-MP9"].includes(wifi))
            type = HotspotType.Concealed;
        else
            type = HotspotType.Custom;

        return new HotspotInfo(wifi, type);
    }

    public get IsAtHome()
    {
        return this.Hotspot.Type == HotspotType.Home
            && this.ConnectionStatus.Is(ConnectionStatus.Connected);
    }

    public get IsHalted(): boolean
    {
        return this.Config?.halt;
    }

    public get IsLocked(): boolean
    {
        return this.Password.value.endsWith("defpass");
    }

    public get IsPrivate(): boolean
    {
        return this.Record?.IsPrivate;
    }
    //#endregion Props

    public MakePictureInfo(): string
    {
        const pictureConfig = this.Config?.picture;

        if (!pictureConfig)
        {
            return "no picture info";
        }

        return `${pictureConfig.name || "(no name)"}: ${pictureConfig.width || "X"} × ${pictureConfig.height || "Y"}`;
    }

    private Emit(event: string, ...params: any[]): void
    {
        if (!this._server.IsConnected)
        {
            alert("Server is not connected. Please refresh the page");
            return;
        }

        console.log(`Emiting "${event}" event to ${this.Id} with params [${params.join(", ")}]...`);
        this._server.socket.emit(event, this.Id, ...params);
    }
  
    public async Request<T>(event: string, params?: any[]): Promise<T>
    {
        return new Promise((resolve, reject) =>
        {
            if (!this._server.IsConnected)
            {
                reject(new Error("Server is not connected. Please refresh the page"));
                return;
            }

            console.log(`Requesting "${event}" from ${this.Id}...`, params ?? "no params");
            this._server.socket.timeout(5000).emit(event, this.Id, params, (err, response) =>
            {
                if (err)
                {
                    reject(err);
                    return;
                }
                resolve(response);
            });
        });
    }

    public SendSettings(settings: DeviceSettingsForAdmin): void
    {
        this.Emit('settings', settings);

        this._server.DeviceSettingsUpdateRequestCache.Add(this.Id);
    }

    //#region Commands
    public SendShellCommand(cmd: string): void
    {
        this.Emit('shell-cmd', cmd);
    }

    private SendCommand(command: string, ...params: any[]): void
    {
        this.Emit('cmd', command, ...params);
    }

    public Kill(): void
    {
        this.SendCommand('die');
    }

    public Reboot(): void
    {
        this.SendShellCommand('sudo reboot');
    }
    //#endregion Commands

    //#region Shell 
    private ShellEngineInit(): void
    {
        this._server.OnEvent.Reset().Subscribe((tid, event, args) =>
        {
            if (tid != this.Id)
            {
                return;
            }

            switch (event)
            {
                case "shell-result":
                    const result = args[0];
                    const cmd = args[1];
                    this.OnShellResult.Call(result, cmd);
                    break;
                case "file":
                    const fileDir = args[0];
                    const fileContent = args[1];
                    this.OnFileLoad.Call(fileDir, fileContent);
                    break;
                case "file-saved":
                    const dir = args[0];
                    this.OnFileSaved.Call(dir);
                    break;
                case "file-problem":
                    const filePro = args[0];
                    this.OnFileProblem.Call(filePro);
                    break;
                case "led-status":
                    const status = args[0];
                    this.OnLedStatusChange.Call(status);
                    break;
            }
        });
    }

    public LoadFile(dir: string): void
    {
        this.Emit('download-file', dir);
    }

    public SaveFile(dir: string, content: string): void
    {
        this.Emit('upload-file', dir, content);
    }
    //#endregion Shell
    
    //#region Leds
    public LedOn(): void
    {
        this.SendCommand('led-engine', 'on');
    }

    public LedOff(): void
    {
        this.SendCommand('led-engine', 'off');
    }

    public SetLeds(greenLed: number, redLed: number): void
    {
        this.SendCommand('led-set', [redLed, greenLed]);
    }

    public RequestLedEngineStatus(): void
    {
        this.SendCommand('led-engine', 'status');
        // This will return 'led-status' event with device id and status (on or off /boolean/)
    }

    public SetLedStatus(status: LedStatus): void
    {
        this.SendCommand('led-status', status);
    }
    //#endregion Leds 
    
    //#region Watchers 
    public KickWatcher(watcherId: string): void
    {
        this.Emit('kick-watcher', watcherId);
    }

    public SendMessageToWatcher(watcherId: string, message: string): void
    {
        this.Emit('message-to-watcher', watcherId, message);
    }
    //#endregion Watchers

    //#region Password
    public async GenerateSMS(): Promise<string>
    {
        if (this.IsLocked) throw new Error("Device is locked. Regenerate password first.");

        const sms = CustomerAccessMessageBuilder.Sms(this.Password.value, this.Id, this._server.Id);

        return sms;
    }

    public async GenerateSMSAndCopyToClipboard(): Promise<string>
    {
        const sms = await this.GenerateSMS();

        await CopyToClipboard(sms);

        return sms;
    }

    public async RegeneratePasswordAndCopySMSMessage(): Promise<void>
    {
        await this.RegeneratePassword();
        await this.GenerateSMSAndCopyToClipboard();
    }

    public async RegeneratePassword(): Promise<void>
    {
        await this._server.Request("regenerate-password", [this.Id],
            (password: string) =>
            {
                this.Password.value = password;
            },
            (errorMessage: string) =>
            {
                throw new Error("Regenerating password problem: " + errorMessage);
            });
    }

    public async ResetPassword(): Promise<void>
    {
        if (this.IsLocked)
            throw new Error("Already locked");

        await this._server.Request("reset-password", [this.Id],
            (password: string) =>
            {
                this.Password.value = password;
            },
            (errorMessage: string) =>
            {
                throw new Error("Resetting password problem: " + errorMessage);
            });
    }

    public async SetPassword(password: string): Promise<boolean>
    {
        return await this._server.Request("set-password", [this.Id, password],
            () =>
            {
                this.Password.value = password;
            },
            (errorMessage: string) =>
            {
                throw new Error("Setting password problem: " + errorMessage);
            });
    }
    //#endregion Password
}
